import { useEffect, useRef, useState } from "react";

// Holder iPad'ens skærm vågen så længe `active` er true. iOS slipper
// auto-locken når sider bliver hidden — så vi re-acquirer ved visibilitychange.
//
// Returnerer { held, supported } så UI kan vise en advarsel hvis browseren
// ikke understøtter Wake Lock API'et (ældre Safari).

export function useWakeLock(active: boolean) {
  const [held, setHeld] = useState(false);
  const supported =
    typeof navigator !== "undefined" && "wakeLock" in navigator;
  const sentinel = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    if (!active || !supported) return;
    let cancelled = false;

    async function acquire() {
      try {
        const s = await navigator.wakeLock.request("screen");
        if (cancelled) {
          await s.release().catch(() => {});
          return;
        }
        sentinel.current = s;
        setHeld(true);
        s.addEventListener("release", () => {
          setHeld(false);
          if (sentinel.current === s) sentinel.current = null;
        });
      } catch {
        // Bruger kan have nægtet, eller siden er ikke i fokus — ignorer.
      }
    }

    acquire();

    function onVisibility() {
      if (document.visibilityState === "visible" && !sentinel.current) {
        acquire();
      }
    }
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      if (sentinel.current) {
        sentinel.current.release().catch(() => {});
        sentinel.current = null;
      }
    };
  }, [active, supported]);

  return { held, supported };
}
